import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import { customTheme } from "../../../../config/theme/MuiTheme";
import FormInput from "../common/FormInput";
import EnumChangeName from "../common/EnumChangeName";
import { vehicleActions } from "../../service/vehicle.actions";
import { useContext, useEffect, useMemo, useState } from "react";
import { ContextOperation } from "../../context/OperationContext";
import { Parser, saveFileFromResponse } from "../../utils";
import OperationTypes from "../../types";

interface Props {
  firstFueling: boolean;
  setFirstFueling: (value: boolean) => void;
  fuelingData: any;
  vehicleId: any;
}

const FirstFueling = (props: Props) => {
  const t = useOperationTranslation();
  const { tenant, setLoading } = useContext(ContextOperation);

  const [vehicleData, setVehicleData] = useState<OperationTypes.Vehicle>();

  const onClose = () => {
    props.setFirstFueling(false);
  };

  const downloadFueling = () => {
    setLoading(true);
    vehicleActions()
      .downloadFuelingData(props.fuelingData.id, tenant)
      .then((response: any) => {
        saveFileFromResponse(response);
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    vehicleActions()
      .getVehicleById(tenant, props.vehicleId)
      .then((res: any) => {
        setLoading(false);
        setVehicleData(res.data as OperationTypes.Vehicle);
      });
  }, []);

  const engineType = useMemo(() => {
    const electric =
      vehicleData?.enginePowerCode === "E" ||
      vehicleData?.enginePowerCode === "HB" ||
      vehicleData?.enginePowerCode === "HD";

    const termic =
      vehicleData?.enginePowerCode === "D" ||
      vehicleData?.enginePowerCode === "G" ||
      vehicleData?.enginePowerCode === "B" ||
      vehicleData?.enginePowerCode === "HB" ||
      vehicleData?.enginePowerCode === "HD";

    if (electric && termic) {
      return "all";
    } else if (electric) {
      return "electric";
    } else if (termic) {
      return "termic";
    }

    return "na";
  }, [vehicleData]);

  return (
    <Mui.Dialog open={props.firstFueling} maxWidth={"md"} fullWidth>
      <Mui.DialogTitle>
        <Mui.Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Mui.Grid item>
            <Mui.Typography variant="h5">
              {t("vehicle.firstFuelling.title")}
            </Mui.Typography>
          </Mui.Grid>
          <Mui.Grid item>
            <Mui.Grid container direction="row" spacing={1.5}>
              {engineType !== "electric" && (
                <Mui.Grid item>
                  <Mui.Tooltip
                    title={t("vehicle.firstFuelling.downloadTooltip")}
                  >
                    <Mui.Button
                      variant="text"
                      size="icon"
                      onClick={downloadFueling}
                    >
                      <MuiIcons.FileDownload />
                    </Mui.Button>
                  </Mui.Tooltip>
                </Mui.Grid>
              )}
              <Mui.Grid item>
                <Mui.Button variant="text" size="icon" onClick={onClose}>
                  <MuiIcons.Close color={"action"} />
                </Mui.Button>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>
      <Mui.DialogContent>
        <Mui.Grid
          container
          columns={12}
          alignItems="center"
          spacing={2.5}
          rowGap={4}
        >
          <Mui.Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <FormInput
              name="firstFuelLocationDesc"
              label={t("vehicle.firstFuelling.firstFuelLocationDesc")}
              value={
                `${
                  props.fuelingData?.firstFuelLocationDesc
                } - ${EnumChangeName().locationType(
                  props.fuelingData?.firstFuelLocationType
                )} ` || ""
              }
              type="text"
              disabled
              fullWidth
            />
          </Mui.Grid>

          <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
            <FormInput
              name="firstFuelDatetime"
              label={t("vehicle.firstFuelling.firstFuelDatetime")}
              value={props.fuelingData?.firstFuelDatetime || ""}
              type="date"
              disabled
              fullWidth
            />
          </Mui.Grid>
          {engineType !== "electric" && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelCardNumber"
                label={t("vehicle.firstFuelling.firstFuelCardNumber")}
                value={
                  props.fuelingData?.firstFuelCardNumber !== undefined
                    ? props.fuelingData?.firstFuelCardNumber
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}
          {(engineType === "all" || engineType === "termic") && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelTermicLiters"
                label={t("vehicle.firstFuelling.firstFuelTermicLiters")}
                value={
                  props.fuelingData?.firstFuelTermicLiters !== undefined &&
                  props.fuelingData?.firstFuelTermicLiters !== "NaN"
                    ? props.fuelingData?.firstFuelTermicLiters
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}

          {(engineType === "all" || engineType === "termic") && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelTermicCostPerLiter"
                label={t("vehicle.firstFuelling.firstFuelTermicCostPerLiter")}
                value={
                  props.fuelingData?.firstFuelTermicCostPerLiter !==
                    undefined &&
                  props.fuelingData?.firstFuelTermicLiters !== "NaN"
                    ? `€ ${Parser.price(
                        props.fuelingData?.firstFuelTermicCostPerLiter
                      )}`
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}

          {(engineType === "all" || engineType === "termic") && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelTotalCostTermic"
                label={t("vehicle.firstFuelling.firstFuelTotalCostTermic")}
                value={
                  props.fuelingData?.firstFuelTotalCostTermic !== undefined
                    ? `€ ${Parser.price(
                        props.fuelingData?.firstFuelTotalCostTermic / 100
                      )}`
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}

          {/* Questo campo è stato rimosso dal first fueling
          (engineType === "all" || engineType === "electric") && 
          <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
            <FormInput
              name="firstFuelElectricKwh"
              label={t("vehicle.firstFuelling.firstFuelElectricKwh")}
              value={
                props.fuelingData?.firstFuelElectricKwh !== undefined
                  ? props.fuelingData?.firstFuelElectricKwh
                  : ""
              }
              type="text"
              disabled
              fullWidth
            />
            </Mui.Grid> */}

          {/* Questo campo è stato rimosso dal first fueling
          (engineType === "all" || engineType === "electric") &&
          <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
            <FormInput
              name="firstFuelElectricCostPerKwh"
              label={t("vehicle.firstFuelling.firstFuelElectricCostPerKwh")}
              value={
                props.fuelingData?.firstFuelElectricCostPerKwh !== undefined
                  ? `€ ${Parser.price(
                      props.fuelingData?.firstFuelElectricCostPerKwh
                    )}`
                  : ""
              }
              type="text"
              disabled
              fullWidth
            />
            </Mui.Grid>*/}

          {(engineType === "all" || engineType === "electric") && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelCardElectricRecharge"
                label={t("vehicle.firstFuelling.firstFuelCardElectricRecharge")}
                value={
                  props.fuelingData?.firstFuelCardElectricRecharge !== undefined
                    ? props.fuelingData?.firstFuelCardElectricRecharge
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}

          {(engineType === "all" || engineType === "electric") && (
            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <FormInput
                name="firstFuelBatteryLevel"
                label={t("vehicle.firstFuelling.firstFuelCardBatteryLevel")}
                value={
                  props.fuelingData?.firstFuelElectricKwh !== undefined
                    ? `${props.fuelingData?.firstFuelElectricKwh}%`
                    : ""
                }
                type="text"
                disabled
                fullWidth
              />
            </Mui.Grid>
          )}
        </Mui.Grid>
      </Mui.DialogContent>

      <Mui.DialogActions>
        <Mui.Button variant="outlined" size="medium" onClick={onClose}>
          <Mui.Typography variant="h6" color={customTheme.palette.accent.main}>
            {t("actionButtons.cancel")}
          </Mui.Typography>
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  );
};

export default FirstFueling;
